/**
 * Build Operate Mission selector groups: Requests, Insights, Incidents —
 * deduped, with resolved labeling.
 */
import type { AmsIncident, ChangeRequest } from '../services/api';
import type { EngineeringRequest } from '../services/engineeringRequests';
import { requestIdOf } from '../services/engineeringRequests';
import { extractAlertFromIncident } from './operationalRemediation';
import { hasAppliedRemediation } from './repoScopedIncidents';
import { scopeInsightId, scopeIncidentId } from '../components/projects/change/changeUtils';
import { incidentStatusLabel as mcIncidentStatusLabel } from '../mission-control/tokens';

export type MissionSelectorKind = 'request' | 'incident';

export type MissionSelectorItem = {
  kind: MissionSelectorKind;
  id: string;
  title: string;
  /** Which section header to show under. */
  section: 'requests' | 'insights' | 'incidents';
  resolved: boolean;
  /** Optional secondary label (status). */
  statusLabel?: string;
};

function isRequestResolved(r: EngineeringRequest): boolean {
  const s = (r.status || '').toLowerCase();
  return (
    s === 'closed' ||
    s === 'completed' ||
    s === 'resolved' ||
    s === 'done' ||
    s === 'cancelled' ||
    s === 'canceled'
  );
}

function isIncidentResolved(inc: AmsIncident): boolean {
  const s = (inc.status || '').toLowerCase();
  if (hasAppliedRemediation(inc)) return true;
  if (s === 'closed' || s === 'resolved') return true;
  const stage = String(inc.mission_stage ?? inc.mission?.current_stage ?? '').toLowerCase();
  return stage === 'closed' || stage === 'closure';
}

/** Sentence case: "in_progress" → "In progress". */
function sentenceCaseLabel(value: string): string {
  const s = value.replace(/_/g, ' ').trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Same human vocabulary as every other incident status badge (mission-control incidentStatusLabel). */
function incidentStatusLabel(inc: AmsIncident): string | undefined {
  if (hasAppliedRemediation(inc) || isIncidentResolved(inc)) return 'Resolved';
  if (!inc.status && !inc.mission_stage) return undefined;
  return mcIncidentStatusLabel(inc.status, inc.mission_stage ?? undefined);
}

function requestStatusLabel(r: EngineeringRequest): string {
  if (isRequestResolved(r)) return 'Resolved';
  const s = (r.status || '').trim();
  if (!s) return '';
  return sentenceCaseLabel(s);
}

/** Insight-originated CR: explicit scope, or external_ref from Insights "Create change request". */
export function isInsightOriginatedCr(cr: ChangeRequest | undefined | null): boolean {
  if (!cr) return false;
  if (scopeInsightId(cr.scope)) return true;
  const ref = (cr.external_ref || '').trim();
  // buildCrFromInsight: CR-{workerId slice}-{insightId slice}
  if (/^CR-[A-Z0-9]{2,}-[a-f0-9-]{6,}/i.test(ref)) return true;
  const src = String((cr.scope as Record<string, unknown> | undefined)?.source || '').toLowerCase();
  return src === 'insight' || src === 'understanding_insight' || src.includes('insight');
}

function isInsightOriginatedRequest(
  r: EngineeringRequest,
  crById: Map<string, ChangeRequest>,
): boolean {
  const ep = (r.entry_point || '').toLowerCase();
  if (ep.includes('insight')) return true;
  const rt = (r.request_type || '').toLowerCase();
  if (rt.includes('insight')) return true;
  const crId = r.mission_reference;
  if (crId && isInsightOriginatedCr(crById.get(crId))) return true;
  return false;
}

/**
 * Build selector items for the Mission dropdown.
 * - Only requests with a mission_reference, plus inbox-scoped incidents.
 * - Insight-originated requests go under Insights.
 * - Incidents already covered by a request's linked CR are omitted (no duplicates).
 * - Within each section: open missions first, then resolved.
 */
export function buildMissionSelectorItems(input: {
  requests: EngineeringRequest[];
  incidents: AmsIncident[];
  changeRequests?: ChangeRequest[];
}): MissionSelectorItem[] {
  const crById = new Map<string, ChangeRequest>();
  for (const cr of input.changeRequests ?? []) {
    if (cr?.id) crById.set(cr.id, cr);
  }

  const missionRequests = (input.requests ?? []).filter((r) => Boolean(r.mission_reference));
  const linkedIncidentIds = new Set<string>();

  for (const r of missionRequests) {
    const crId = r.mission_reference!;
    const cr = crById.get(crId);
    const fromScope = cr ? scopeIncidentId(cr.scope) : undefined;
    if (fromScope) linkedIncidentIds.add(fromScope);
    for (const inc of input.incidents) {
      if (inc.change_request_id && inc.change_request_id === crId) {
        linkedIncidentIds.add(inc.id);
      }
    }
  }

  const requestItems: MissionSelectorItem[] = [];
  const insightItems: MissionSelectorItem[] = [];
  const seenRequestIds = new Set<string>();

  for (const r of missionRequests) {
    const id = requestIdOf(r);
    if (!id || seenRequestIds.has(id)) continue;
    seenRequestIds.add(id);
    const item: MissionSelectorItem = {
      kind: 'request',
      id,
      title: r.title || 'Untitled request',
      section: isInsightOriginatedRequest(r, crById) ? 'insights' : 'requests',
      resolved: isRequestResolved(r),
      statusLabel: requestStatusLabel(r),
    };
    if (item.section === 'insights') insightItems.push(item);
    else requestItems.push(item);
  }

  const seenIncidentIds = new Set<string>();
  const incidentItems: MissionSelectorItem[] = [];
  for (const inc of input.incidents) {
    if (!inc?.id || seenIncidentIds.has(inc.id)) continue;
    if (linkedIncidentIds.has(inc.id)) continue;
    seenIncidentIds.add(inc.id);
    const title = extractAlertFromIncident(inc)?.signal || inc.title || 'Untitled incident';
    incidentItems.push({
      kind: 'incident',
      id: inc.id,
      title,
      section: 'incidents',
      resolved: isIncidentResolved(inc),
      statusLabel: incidentStatusLabel(inc) || undefined,
    });
  }

  const sortSection = (items: MissionSelectorItem[]) =>
    [...items].sort((a, b) => {
      if (a.resolved !== b.resolved) return a.resolved ? 1 : -1;
      return a.title.localeCompare(b.title);
    });

  return [
    ...sortSection(requestItems),
    ...sortSection(insightItems),
    ...sortSection(incidentItems),
  ];
}

/** Compact mission metadata for the bar beside the selector (no duplicate CLOSED). */
export function missionBarMeta(parts: {
  recognition?: string;
  applicationKey?: string;
  severity?: string;
  resolved?: boolean;
  statusLabel?: string;
}): string {
  const out: string[] = [];
  if (parts.recognition) out.push(parts.recognition);
  if (parts.applicationKey) out.push(parts.applicationKey);
  if (parts.severity) out.push(`${sentenceCaseLabel(parts.severity)} severity`);
  if (parts.resolved) {
    out.push('Resolved');
  } else if (parts.statusLabel) {
    const s = parts.statusLabel.trim();
    if (s && s.toLowerCase() !== 'closed') out.push(s);
    else if (s.toLowerCase() === 'closed') out.push('Resolved');
  }
  // Dedupe adjacent/identical tokens (e.g. Fixed + Resolved is fine; CLOSED×2 is not)
  const deduped: string[] = [];
  for (const p of out) {
    const prev = deduped[deduped.length - 1];
    if (prev && prev.toLowerCase() === p.toLowerCase()) continue;
    deduped.push(p);
  }
  return deduped.join(' · ');
}
